import { Controller } from "@hotwired/stimulus"
import { parseStartedAt, formatElapsed } from "../components/lib/call_duration"

// CLA-82: header call control -- a dumb view over video_call_controller.js's
// state machine plus its own live duration timer. It never decides what a
// click means (that's handleEnter()'s job on the other side, D1) and never
// owns call state -- it only mirrors it and renders elapsed time.
//
// This controller mounts on the header's non-permanent wrapper, so unlike
// video_call_controller.js (mounted once inside the permanent shell) it
// disconnects and reconnects on every Turbo visit for the whole duration of
// a call (D12). That is why the tick lifecycle in start/stopTicking below is
// disciplined rather than "start once and forget": an unguarded interval
// here would accrue one live timer per navigation for the whole call.
export default class extends Controller {
  static targets = ["idleState", "incomingState", "activeState", "duration"]

  static values = {
    state: { type: String, default: "idle" },
    startedAt: { type: String, default: "" },
    // Opt-in for the tapbar item: hides the whole element (not just a state
    // target) when there is nothing to show, i.e. a student while idle.
    collapseWhenEmpty: { type: Boolean, default: false },
  }

  // Review fix (code review blocker): Stimulus's ValueObserver invokes
  // `stateValueChanged` synchronously as part of binding the initial DOM
  // attribute -- BEFORE `connect()` runs (documented at
  // video_call_controller.js:27-35; `_call_button.html.erb` always renders
  // `data-call-button-state-value`, so this pre-connect call always fires
  // here too). That first `stateValueChanged` call reaches `_renderFallback()`,
  // so the fallback label must already be cached by then -- caching it in
  // `connect()` instead let that very call write `this._fallbackLabel` as
  // `undefined` (DOM-coerced to `""`), permanently destroying the
  // server-rendered "Звонок идёт" for the life of the mount. `initialize()`
  // runs at Context construction, before the value observer starts, and
  // `this.scope` is already set then, so `hasDurationTarget`/`durationTarget`
  // resolve correctly here.
  initialize() {
    this._fallbackLabel = this.hasDurationTarget ? this.durationTarget.textContent : ""
  }

  connect() {
    this.boundHandleState = this.handleState.bind(this)
    document.addEventListener("video-call:state", this.boundHandleState)

    // D5: the connect-time pull is required, not optional -- this header
    // instance will *miss* any `video-call:state` dispatch that happened
    // before it connected (e.g. the widget went "active" on a previous
    // page), so it cannot rely on the event alone for the initial read.
    this.syncFromWidget()

    this.boundSyncFromWidget = this.syncFromWidget.bind(this)
    // Cross-URL same-class visit: the header re-renders fresh (server sends
    // "incoming") while the permanent shell still holds "active" client-side
    // state from before the navigation.
    document.addEventListener("turbo:load", this.boundSyncFromWidget)
    // Refresh-visit path (same-URL): app/views/layouts/application.html.erb:7
    // sets <meta name="turbo-refresh-method" content="morph">, so under
    // Turbo 8 a same-URL refresh reconciles this non-permanent header back
    // to the server's "incoming" while the permanent shell keeps "active" --
    // and neither `turbo:load` nor `connect()` fires for that path. This is
    // reachable (test 9.10 drives it), not a dead listener -- do not remove.
    document.addEventListener("turbo:morph", this.boundSyncFromWidget)
  }

  disconnect() {
    document.removeEventListener("video-call:state", this.boundHandleState)
    document.removeEventListener("turbo:load", this.boundSyncFromWidget)
    document.removeEventListener("turbo:morph", this.boundSyncFromWidget)
    this._stopTicking()
  }

  // D2: the header dispatches `chat:open` itself, then `video-call:enter` --
  // synchronous, no `await` between them. DOM dispatch is synchronous, so
  // chat_controller#handleOpen -> openValueChanged runs to completion (panel
  // un-hidden) before handleEnter() on the other side is even invoked, and
  // `startCall()`'s later `await` only defers work that already requires the
  // panel to be open. Never `chat:toggle`: in "active" state it would close
  // an already-open panel -- the opposite of "take me back to the call" --
  // and it would mark the conversation read. Never route through
  // video_call_controller's own `_dispatchChatOpen()`: in "idle",
  // `videoSessionIdValue` is still 0, so a future reader pairing it with
  // `_markAutoOpened()` would stamp "0" and the real session's marker would
  // never match, silently reintroducing CLA-80's reopen bug.
  enter() {
    document.dispatchEvent(new CustomEvent("chat:open"))
    document.dispatchEvent(new CustomEvent("video-call:enter"))
  }

  // video_call_controller.js's `stateValueChanged` dispatches this
  // unconditionally with the full { state, startedAt } payload -- write
  // `startedAt` first so `stateValueChanged` below never observes "active"
  // with a stale startedAt from a previous call.
  handleState(event) {
    this.startedAtValue = event.detail?.startedAt || ""
    this.stateValue = event.detail?.state || "idle"
  }

  // D5: reads the widget's own attributes directly rather than trusting a
  // rendered fact -- the Bardo placeholder (a bare
  // <meta name="turbo-permanent-placeholder">) does not match this selector
  // at all during Turbo's permanent-element restoration, so the query finds
  // nothing and this must no-op: leave the current state, never fall back to
  // "idle". D3: no role branch here either -- a student without an
  // `idleState` target simply no-ops that toggle later via `hasIdleStateTarget`.
  syncFromWidget() {
    const widget = document.querySelector('[data-controller~="video-call"]')
    if (!widget) return

    this.startedAtValue = widget.getAttribute("data-video-call-started-at-value") || ""
    this.stateValue = widget.getAttribute("data-video-call-state-value") || "idle"

    // Review fix: on a genuine morph refresh, idiomorph patches the header's
    // own `data-call-button-state-value` attribute back to the server's
    // value (e.g. "incoming") as part of reconciling this non-permanent node
    // to its stale server-rendered markup -- in the SAME synchronous batch
    // this `turbo:morph` handler runs in, immediately followed by the write
    // above restoring it to the permanent shell's real value (e.g. "active").
    // Stimulus's ValueObserver is driven by MutationObserver records, and a
    // net-zero attribute change within one microtask batch (active -> morph
    // sets incoming -> we set it back to active) never fires
    // `stateValueChanged` at all -- so the class toggle it drives would
    // silently never run, leaving the classes idiomorph wrote (the wrong,
    // stale ones) on screen while `stateValue`/the DOM attribute are already
    // correct. Calling the shared render routine explicitly, unconditionally,
    // closes that gap without abandoning the single-funnel rule: on every
    // ordinary path `stateValueChanged` still fires and drives this exact
    // same logic, so this call is then a harmless idempotent no-op (class
    // toggles are already correct; `_syncTick()`'s own guards keep it from
    // starting a second interval).
    this._render(this.stateValue)
  }

  // Single funnel (5.2): every state-driven effect -- the three sibling
  // buttons' visibility AND the tick lifecycle -- goes through here, because
  // every path that can change state (connect()'s syncFromWidget(),
  // handleState(), and the turbo:load/turbo:morph re-syncs) writes
  // `stateValue` rather than touching the DOM or the timer directly. Ticking
  // is deliberately NOT started from connect() or handleState() directly:
  // the turbo:load re-sync is the path that carries an "incoming" -> "active"
  // transition through neither of those (a mid-call navigation, test 9.21),
  // so funneling through `_render()` is the only way to not miss it.
  stateValueChanged(value) {
    this._render(value)
  }

  // The actual funnel body, factored out so `syncFromWidget()` can also
  // invoke it explicitly on the morph path above, where `stateValueChanged`
  // itself is provably never called (see that comment). Both call sites pass
  // `this.stateValue`, so this always renders the controller's current,
  // already-correct value -- it never diverges from what `stateValueChanged`
  // would have rendered on its own.
  _render(value) {
    // The `hidden` attribute, not a class: BottomNav::Link spreads html_options
    // after its own class:. Lives here so syncFromWidget()'s explicit _render()
    // heals a morph that reverted it.
    if (this.collapseWhenEmptyValue) {
      this.element.hidden = value === "idle" && !this.hasIdleStateTarget
    }
    if (this.hasIdleStateTarget) {
      this.idleStateTarget.classList.toggle("hidden", value !== "idle")
    }
    if (this.hasIncomingStateTarget) {
      this.incomingStateTarget.classList.toggle("hidden", value !== "incoming")
    }
    if (this.hasActiveStateTarget) {
      this.activeStateTarget.classList.toggle("hidden", value !== "active")
    }

    this._syncTick()
  }

  // Review fix (should-fix): in production `video_call_controller.js` only
  // ever writes `startedAtValue` immediately before a `stateValue` change, so
  // this is unreachable as an independent transition today -- but without
  // this callback, a `startedAt`-only change would dead-end in two ways: (1)
  // "active" with an unknown start time, later corrected to a real past time,
  // never starts the tick until an unrelated `turbo:load`/`turbo:morph` heals
  // it; (2) once `_tick()` self-stops on a bad value, a later good value never
  // restarts it -- a one-way trapdoor. Delegating into the same `_syncTick()`
  // funnel `stateValueChanged` uses keeps the "every state-driven effect goes
  // through one funnel" claim actually true, and stays idempotent given the
  // existing write ordering (re-deriving the same decision `stateValueChanged`
  // would have made is a no-op when nothing has changed).
  startedAtValueChanged() {
    this._syncTick()
  }

  // Shared decision between the two *ValueChanged callbacks above: start
  // ticking only for "active" with a known start time; stop and show the
  // fallback for every other combination (leaving "active", idle/incoming,
  // or "active" with an unknown start time) -- an always-on 1 Hz interval on
  // every class page is both the leak surface (D12) and needless work.
  //
  // Review fix: `_tick()` runs unconditionally after `_startTicking()`, not
  // only inside it. `_startTicking()`'s own synchronous tick (D12's "tick
  // once before installing the interval") only fires on a genuine start --
  // its `_tickId` guard no-ops when an interval survives from before this
  // call, which is exactly what happens on a morph refresh (the interval
  // never stopped, because the controller never disconnected). Idiomorph
  // patches the duration span's `textContent` back to the server-rendered
  // fallback as part of reconciling this non-permanent node, and without a
  // synchronous tick here that stale fallback would sit on screen until the
  // next 1s interval fire. Calling `_tick()` again here is a harmless
  // no-op re-render on every ordinary start (it already ran once inside
  // `_startTicking()`), so this doesn't double the interval or the tick
  // count in any way that matters -- it only ever adds a second render pass
  // with identical output.
  _syncTick() {
    const startedAtMs = parseStartedAt(this.startedAtValue)
    // No duration target (tapbar item): starting a tick would run a second 1 Hz interval for nothing.
    if (this.hasDurationTarget && this.stateValue === "active" && this._isKnownStart(startedAtMs)) {
      this._startTicking()
      this._tick()
    } else {
      this._stopTicking()
      this._renderFallback()
    }
  }

  // Absent, unparseable, or more than 60s in the future are all treated as
  // "unknown" -- the 60s threshold sits above ordinary NTP drift, below any
  // skew that would make a rendered duration actively mislead, and an order
  // of magnitude above the 1s tick interval so the control cannot flap
  // between a duration and the fallback label. Takes the already-parsed
  // ms-or-null rather than re-parsing, so a single tick only ever calls
  // `parseStartedAt` once (see `_tick()`).
  _isKnownStart(startedAtMs) {
    return startedAtMs !== null && startedAtMs <= Date.now() + 60_000
  }

  // Ticks once synchronously before installing the interval so the duration
  // never shows a stale/empty value for the first second. No-ops when
  // `_tickId` is already set (mirrors `_stopTicking()`'s undefined/null
  // check below) so a churn of disconnect() -> connect() within the same
  // call (a same-class Turbo visit) can't stack a second interval on top of
  // the one already running.
  _startTicking() {
    if (this._tickId !== undefined && this._tickId !== null) return

    this._tick()
    this._tickId = window.setInterval(() => this._tick(), 1000)

    // CLA-82: leak-test instrumentation, same idiom as
    // video_call_controller.js's __videoCallConnectCount and VideoPanel.jsx's
    // own interval bookkeeping.
    window.__callButtonIntervalIds = window.__callButtonIntervalIds || new Set()
    window.__callButtonIntervalIds.add(this._tickId)
  }

  // Clears AND nulls `_tickId` so the `_startTicking()` guard above stays
  // honest across a disconnect() -> connect() churn -- a stale non-null id
  // left behind would make a later, genuine restart silently no-op.
  _stopTicking() {
    if (this._tickId === undefined || this._tickId === null) return

    window.clearInterval(this._tickId)
    window.__callButtonIntervalIds?.delete(this._tickId)
    this._tickId = null
  }

  // Re-reads `startedAtValue` on every single tick rather than freezing a
  // baseline at `_startTicking()` time: `syncFromWidget()` can correct
  // `startedAtValue` *after* the state is already "active" (test 9.26
  // rewrites it mid-call and expects the rendered value to jump within one
  // tick), and a frozen baseline would swallow that correction. Computes
  // elapsed from `Date.now()` against the freshly-read value each time --
  // never accumulates a running counter, since Chrome throttles background
  // tabs to ~1/min and an accumulating counter would lose real minutes.
  _tick() {
    window.__callButtonTickCount = (window.__callButtonTickCount || 0) + 1

    const startedAtMs = parseStartedAt(this.startedAtValue)
    if (!this._isKnownStart(startedAtMs)) {
      this._renderFallback()
      this._stopTicking()
      return
    }

    // Clamp at zero -- an accurate 0:00 (e.g. a tick landing right at call
    // start) is permitted; it is not the forbidden fabricated zero (D12),
    // which is a zero shown because the start time is unknown.
    const elapsedSeconds = Math.max(0, Math.floor((Date.now() - startedAtMs) / 1000))
    if (this.hasDurationTarget) {
      this.durationTarget.textContent = formatElapsed(elapsedSeconds)
    }
  }

  _renderFallback() {
    if (this.hasDurationTarget) {
      this.durationTarget.textContent = this._fallbackLabel
    }
  }
}
